import React, { useState, useRef } from 'react';
import classnames from 'classnames';

import {
  Typography,
  Chip,
  Popper,
  Paper,
  MenuList,
  ButtonBase
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Item } from '@hydra/common/components/Header/menuItems';
import { Icon, MenuItem, Translate } from '@hydra/common/components';

import { useI18n } from '@hydra/common/customHooks';

import { sendEvent } from '@hydra/utils/gtm';
import getTranslatedUrl from '@hydra/utils/getTranslatedUrl';

const useStyles = makeStyles(theme => ({
  root: {
    boxShadow: '0 5px 5px 0 rgb(0 0 0 / 10%)'
  },
  fullBackground: {
    left: `${theme.spacing(0.5)}px !important`
  },
  header: {
    pointerEvents: 'none',
    paddingTop: 0,
    paddingBottom: 0,
    opacity: '1 !important', // Avoid opacity when element is disabled
    '& > span': {
      fontWeight: theme.typography.fontWeightSemibold,
      fontSize: theme.spacing(1.375),
      color: theme.palette.grey[500],
      textTransform: 'uppercase'
    },
    '&:focus, &:hover, &:active': {
      textDecoration: 'none'
    }
  },
  menuList: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 0),
    '& > div': {
      top: `${theme.spacing(8)}px !important`
    },
    '& > div > ul': {
      display: 'flex'
    }
  },
  menuListFull: {
    width: '100vw',
    padding: theme.spacing(3.75, 0, 10, 0),
    '& > ul': {
      padding: theme.spacing(0, 8)
    }
  },
  helpMenuItem: {
    minWidth: 255,
    maxWidth: 'fit-content',
    width: 'auto',
    fontSize: theme.typography.fontSizeLarge,
    padding: theme.spacing(1, 2.5),
    '&:hover': {
      backgroundColor: 'unset',
      textDecoration: 'underline',
      '& $helpListItemIcon': {
        '& > svg': {
          fill: theme.palette.primary.main
        }
      },
      '& $externalListIconItem': {
        '& > svg': {
          display: 'inline-block',
          fill: theme.palette.primary.main
        }
      }
    }
  },
  helpListItemIcon: {},
  externalListIconItem: {
    marginLeft: 'auto',
    minWidth: theme.spacing(3),
    '& > svg': {
      display: 'none',
      width: theme.spacing(2),
      height: theme.spacing(2)
    }
  },
  menuItemText: {
    display: 'inline-flex',
    fontSize: theme.spacing(2.375)
  },
  arrowIcon: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.25)
  },
  chip: {
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.fontSizeSmall,
    height: theme.spacing(2.25),
    alignSelf: 'center'
  }
}));

interface IRenderTriggerProps {
  'aria-expanded': boolean | undefined
  disableElevation: boolean
  onMouseEnter: React.MouseEventHandler,
  onMouseLeave: React.MouseEventHandler,
  disabled: boolean
  endIcon: React.ReactNode
}

interface IMenuDropdownProps {
  renderTrigger: (props: IRenderTriggerProps) => React.ReactNode
  contentColor: 'white' | 'gray'
  menuItems: Item[][]
}

export default function MenuDropdown ({ renderTrigger, contentColor, menuItems }: IMenuDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const classes = useStyles();
  const { locale } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);

  function checkMousePoint (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>, el: HTMLDivElement | HTMLButtonElement | null): boolean {
    if (!el) {
      return false;
    }
    const { top, bottom, left, right } = el.getBoundingClientRect();
    const { clientX, clientY } = event;
    if (clientX >= left && clientX <= right && clientY >= top && clientY <= bottom) {
      return true;
    }
    return false;
  }

  function handleMouseEnter (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    setAnchorEl(event.currentTarget);
  }

  function handleMouseLeave (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (checkMousePoint(event, containerRef.current)) {
      return;
    }
    setAnchorEl(null);
  }

  function handleMouseLeaveOnContainer (event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (checkMousePoint(event, anchorEl)) {
      return;
    }
    setAnchorEl(null);
  }

  function handleGtmClick (label = '') {
    if (label) {
      sendEvent(label);
    }
  }

  const triggerComponentProps = {
    'aria-expanded': anchorEl ? true : undefined,
    disableElevation: false,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    disabled: false,
    endIcon: <Icon type='chevron-down' color={contentColor} className={classes.arrowIcon} />
  };

  return (
    <>
      {renderTrigger(triggerComponentProps)}
      <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} className={classnames(classes.root, { [classes.fullBackground]: menuItems.length > 1 })} role={undefined} placement='bottom-start' disablePortal>
        <Paper ref={containerRef} onMouseLeave={handleMouseLeaveOnContainer} className={classnames(classes.menuList, { [classes.menuListFull]: menuItems.length > 1 })}>
          {
            menuItems.map((items: Item[], gIndex: number) => (
              <MenuList disablePadding key={gIndex}>
                {
                  items.map((item: Item, index: number) => (
                    <MenuItem
                      key={index}
                      href={item.externalLink ? item.externalLink : item.link && getTranslatedUrl(item.link, locale)}
                      component={ButtonBase}
                      disabled={item.header}
                      onClick={() => { handleGtmClick(item.gtmLabel); }}
                      className={classnames(
                        classes.helpMenuItem,
                        item.className,
                        {
                          [classes.header]: item.header
                        }
                      )}
                    >
                      <Typography noWrap component='span' className={classes.menuItemText}>
                        <Translate content={item.label} />
                        {item.chipTag && (
                          <Chip label={<Translate content={item.chipTag} />} className={classes.chip} color='primary' size='small' />
                        )}
                      </Typography>
                    </MenuItem>
                  ))
                }
              </MenuList>
            ))
          }
        </Paper>
      </Popper>
    </>
  );
}
